/**
 * The sign-in gate. No session → redirect to hosted login; /callback → finish the code
 * exchange and land on /. Children only render with a live access token in hand.
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { UserManager } from "oidc-client-ts";
import type { AppConfig } from "../config";
import { createUserManager, logoutUrl, sessionFrom, type Session } from "../auth/session";
import { navigate, useRoute } from "./router";

type AuthState = { session: Session; signOut: () => void; refresh: () => Promise<void> };
const Ctx = createContext<AuthState | null>(null);

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

export function AuthProvider({ cfg, children }: { cfg: AppConfig; children: ReactNode }) {
  const origin = window.location.origin;
  const um: UserManager = useMemo(() => createUserManager(cfg, origin), [cfg, origin]);
  const route = useRoute();
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (route === "/callback") {
        await um.signinCallback();
        navigate("/subjects");
        return;
      }
      const user = await um.getUser();
      const s = sessionFrom(user);
      if (!s) {
        await um.signinRedirect();
        return;
      }
      if (!cancelled) setSession(s);
    })();
    const onLoaded = (u: Parameters<typeof sessionFrom>[0]) => setSession(sessionFrom(u));
    um.events.addUserLoaded(onLoaded);
    return () => {
      cancelled = true;
      um.events.removeUserLoaded(onLoaded);
    };
  }, [um, route]);

  if (session === undefined || session === null) return <p className="muted">Signing in…</p>;

  const value: AuthState = {
    session,
    signOut: () => {
      void um.removeUser();
      window.location.assign(logoutUrl(cfg, origin));
    },
    refresh: async () => setSession(sessionFrom(await um.signinSilent())),
  };
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
